import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, ShieldCheck, Building2, Stethoscope, Loader2 } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useCareRecipientStore } from "@/store/careRecipientStore";
import { atHomeApi, referralApi, customerApi } from "@/lib/api";

const REASONS = ["Vitals check", "Injection / IV", "Wound dressing", "Post-surgical care", "Elderly care", "Catheter care", "General check-up"];
const SLOTS = ["08:00 AM - 10:00 AM", "10:00 AM - 12:00 PM", "02:00 PM - 04:00 PM", "05:00 PM - 07:00 PM"];
const SAMPLE = [
  { id: "careplus", name: "CarePlus Home Healthcare", area: "Indira Nagar", distance: "3.2 km", rating: "4.8", reviews: "320" },
  { id: "medcare", name: "MedCare Nursing Services", area: "Gomti Nagar", distance: "5.1 km", rating: "4.6", reviews: "210" },
  { id: "citycare", name: "CityCare Hospital (Home Care)", area: "Mahanagar", distance: "6.8 km", rating: "4.5", reviews: "189" },
];

function isoDate(d) {
  if (!d) { const t = new Date(); t.setDate(t.getDate() + 1); return t.toISOString().slice(0, 10); }
  return d;
}

export default function SeniorCareBookNurse() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const name = useAuthStore((s) => s.displayName());
  const { recipient } = useCareRecipientStore();
  const uid = user?._id;

  const [reason, setReason] = useState("");
  const [address, setAddress] = useState("");
  const [date, setDate] = useState(isoDate());
  const [slot, setSlot] = useState(SLOTS[1]);
  const [providers, setProviders] = useState(SAMPLE);
  const [booking, setBooking] = useState(false);

  const patientName = recipient?.name || name || "Myself";
  const patientAge = recipient?.age;

  const load = useCallback(async () => {
    if (!uid) return;
    try {
      const prof = await customerApi.getProfile(uid);
      const b = prof.data?.basicInfo || prof.data?.profile?.basicInfo || {};
      const addr = [b.address, b.city, b.pincode].filter(Boolean).join(", ");
      if (addr) setAddress(addr);
      const { data } = await referralApi.onboarded(b.city ? { city: b.city } : {});
      const real = (data?.hospitals || []).map((h) => ({ id: h.id, name: `${h.name} (Home Care)`, area: h.city || "", distance: "", rating: "4.6", reviews: "—", real: true }));
      if (real.length) setProviders([...real, ...SAMPLE].slice(0, 4));
    } catch { /* keep samples */ }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  const submit = async (provider) => {
    if (!reason) { toast.error("Please choose a reason for the visit."); return; }
    if (!address.trim()) { toast.error("Please enter the service address."); return; }
    setBooking(true);
    try {
      const res = await atHomeApi.book({
        userId: uid,
        serviceType: "nursing",
        serviceName: "Nurse Visit",
        patientName,
        patientPhone: recipient?.phone || user?.phone || "",
        patientAge: patientAge ?? null,
        patientGender: recipient?.gender || null,
        address,
        bookingDate: `${date}T00:00:00`,
        timeSlot: slot,
        serviceData: {
          ...(recipient ? { forRecipientId: recipient.id, forRecipientName: recipient.name, forRelation: recipient.relation, bookedByGuardian: true } : {}),
          reason,
          providerPreference: provider ? provider.name : "auto",
        },
        providerId: provider?.real ? provider.id : undefined,
        providerName: provider?.real ? provider.name : undefined,
        notes: `Reason: ${reason}${provider ? `\nPreferred provider: ${provider.name}` : "\nLet Trata choose the best provider"}`,
      });
      if (res.data?.success) {
        toast.success("Nurse Visit Booked!", { description: `${provider ? provider.name : "Trata"} will attend ${patientName}.` });
        navigate(-1);
      }
    } catch { toast.error("Booking failed. Please try again."); }
    finally { setBooking(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} data-testid="booknurse-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="text-2xl font-extrabold" style={{ color: "#1E3A8A" }}>Book a Nurse Visit</h1>

      {/* Selected senior */}
      <div className="flex items-center gap-3 rounded-2xl p-4" style={{ backgroundColor: "#E0EAFF" }}>
        <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center text-xl font-extrabold" style={{ color: "#2563EB" }}>{patientName.charAt(0).toUpperCase()}</div>
        <div className="flex-1">
          <p className="text-xs text-slate-600">{recipient ? "Selected Senior" : "Booking for"}</p>
          <p className="text-lg font-extrabold" style={{ color: "#1E3A8A" }}>{patientName}</p>
          {patientAge != null && <p className="text-xs text-slate-600">Age {patientAge}</p>}
        </div>
        <button onClick={() => navigate(-1)} data-testid="booknurse-change-senior" className="text-sm font-bold" style={{ color: "#2563EB" }}>Change</button>
      </div>

      {/* Reason */}
      <div>
        <label className="block text-sm font-bold mb-2">Reason for visit</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="booknurse-reason" className="w-full rounded-xl border border-border bg-card px-3 h-12 text-sm">
          <option value="">Select reason</option>
          {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Address */}
      <div>
        <label className="block text-sm font-bold mb-2">Address</label>
        <div className="flex gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
          <MapPin className="h-4 w-4 mt-1 shrink-0" style={{ color: "#2563EB" }} />
          <textarea value={address} onChange={(e) => setAddress(e.target.value)} data-testid="booknurse-address" placeholder="Enter the service address" rows={2} className="flex-1 bg-transparent text-sm outline-none resize-none" />
        </div>
      </div>

      {/* Date + time */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-bold mb-2">Preferred date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="booknurse-date" className="w-full rounded-xl border border-border bg-card px-3 h-12 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-bold mb-2">Preferred time</label>
          <select value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="booknurse-time" className="w-full rounded-xl border border-border bg-card px-3 h-12 text-sm">
            {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Providers */}
      <h2 className="text-lg font-extrabold" style={{ color: "#1E3A8A" }}>Recommended Providers</h2>
      <div className="space-y-3">
        {providers.map((p) => (
          <div key={p.id} data-testid={`booknurse-provider-${p.id}`} className="flex gap-3 rounded-2xl border border-border bg-card p-4">
            <div className="h-12 w-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#EFF6FF" }}>
              {p.real ? <Building2 className="h-5 w-5" style={{ color: "#2563EB" }} /> : <Stethoscope className="h-5 w-5" style={{ color: "#2563EB" }} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{p.name}</p>
              <p className="text-xs text-muted-foreground">{p.area}</p>
              <p className="text-xs text-slate-600 mt-1">{p.distance ? `📍 ${p.distance}  ·  ` : ""}⭐ {p.rating} ({p.reviews})</p>
              <span className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-[10px] font-bold mt-1.5" style={{ backgroundColor: "#DCFCE7", color: "#15803D" }}>
                <ShieldCheck className="h-3 w-3" /> Verified Trata Health Provider
              </span>
            </div>
            <button onClick={() => submit(p)} disabled={booking} data-testid={`booknurse-book-${p.id}`} className="self-center rounded-xl px-5 py-2.5 text-white font-bold" style={{ backgroundColor: "#2563EB" }}>Book</button>
          </div>
        ))}
      </div>

      <button onClick={() => submit(null)} disabled={booking} data-testid="booknurse-auto" className="w-full flex items-center justify-center gap-2 rounded-2xl py-4 font-extrabold" style={{ border: "1.5px dashed #BFDBFE", backgroundColor: "#EFF6FF", color: "#2563EB" }}>
        {booking ? <Loader2 className="h-5 w-5 animate-spin" /> : "Let Trata choose the best available provider (Recommended)"}
      </button>
    </div>
  );
}
